// Accounting › Ledger and reports (Blueprint's; pages/ux-full accounting/ledger), its own module out of
// Books.tsx (BL-189): PageHead with the ten views as its tabs, then the view, a Section each. The reports
// (profit and loss, balance sheet, cash flow) are LineTables with no head row, each section's sum and the
// report's total rows of their own; the trial balance and job costing are LineTables with a foot. Where a
// report stands (Balanced, Ties to the bank) is its Section's state. The journal's entries are RecordRows
// that open in place on their Dr/Cr lines and Reverse, which asks for its reason in a FormDialog. A new
// entry is fields in a Section, its line grid in ledger-page.css. Close a month draws Close a month's own
// parts (CloseChecklist, the months' RowList, Reopen behind its reason). The accountant pack's files are a
// RowList with a Save each. The accounting system moved to Setup › Connected systems; back from the
// provider, Connections still says how connecting went (a declared one-off until it's ported). Riverstone
// keeps Ledger.tsx; ledger-page.css lays out the fields and lines, tokens only.
import {
  Button,
  CloseChecklist,
  ConfirmDialog,
  FormDialog,
  Label,
  LineTable,
  Link,
  monthRows,
  RecordList,
  RecordRow,
  RowList,
  SelectField,
  Skeleton,
  SkeletonGroup,
  TextField,
  type LineRow,
  type MarkState,
  type Row,
} from '@flowology/ui-web/fl';
import { monthWords, type CloseChecksProps } from '@flowology/ui-web';
import { PageHead, Section } from '@flowology/ui-web/patterns';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { client } from '../api.ts';
import { officeHost } from '../blueprint/host.ts';
import { BEFORE_THE_SWITCH, useBringingOver } from '../migration/bringing-over.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { useDocFocus } from '../shared/doc-link.ts';
import { holdsAny } from '../shared/may.ts';
import { Connections, ledgerReturnUntold } from './Connections.tsx';
import {
  notReversibleWhy,
  PACK_PERMS,
  packFileLabel,
  rangeProblem,
  REOPEN_PERMS,
  reopenable,
  reopenWarning,
  REVERSE_PERMS,
  reversedText,
  reversible,
  rowsText,
  type PackFile,
  type Period,
} from './corrections-model.ts';
import { FIX_PART } from './money-lists-model.ts';
import {
  closedMonthHint,
  dayText,
  entryKind,
  money,
  monthName,
  monthOf,
  parseMoney,
  previousMonth,
  seesPayroll,
  todayIn,
} from './model.ts';
import { BOOKS_PARTS } from './parts.ts';
import type { BooksPart } from './props.ts';
import type { Account, BalanceSheet, JournalEntry, ProfitAndLoss, ReportRow, TrialBalance } from './types.ts';
import { errorText, useAct, useQuery } from './ui.tsx';
import './ledger-page.css';

const PART = BOOKS_PARTS.find((p) => p.part === 'ledger')!;
const host = officeHost(() => undefined);

export type LedgerView = Tab;
type Tab = 'journal' | 'trial' | 'pl' | 'bs' | 'cash' | 'jobs' | 'entry' | 'close' | 'connect' | 'pack';

/** Newest first by posting date, then by entry number (JE-14 after JE-6). */
const byDateThenNumber = (a: JournalEntry, b: JournalEntry) =>
  b.postingDate.localeCompare(a.postingDate) ||
  Number(b.number.replace(/\D/g, '')) - Number(a.number.replace(/\D/g, ''));

/** When a month was closed, on this device's calendar: "Oct 15". */
const closedOn = (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric' }).format(new Date(iso));

const standing = (ok: boolean, yes: string, no: string): { mark: MarkState; word: string } =>
  ok ? { mark: 'done', word: yes } : { mark: 'problem', word: no };

/** Under books with nothing in them while the company is still bringing its old system's data over. */
function BeforeTheSwitch() {
  return useBringingOver() ? <p className="bp-ledger-say">{BEFORE_THE_SWITCH}</p> : null;
}

/** A view still loading (skeleton rows), or one that didn't load (a red sentence and Try again). */
function Waiting({ error, what, onRetry }: { error: string | null; what: string; onRetry: () => void }) {
  return error ? (
    <p className="bp-ledger-bad" role="alert">
      <span>{error}</span>
      <Link onPress={onRetry}>Try again</Link>
    </p>
  ) : (
    <SkeletonGroup label={`Loading ${what}`}>
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} shape="row" width="full" />
      ))}
    </SkeletonGroup>
  );
}

const ACCOUNT_COLUMNS = [
  { id: 'account', label: 'Account' },
  { id: 'amount', label: 'Amount', align: 'end' as const },
];

/** An account's line: its code muted, then its name. */
const accountCell = (code: string, name: string): ReactNode =>
  code ? (
    <>
      <span className="bp-ledger-code">{code}</span> {name}
    </>
  ) : (
    name
  );

/** A section of a report: its accounts, then its sum ("Income"). */
const sectionRows = (rows: readonly ReportRow[], total: number, label: string, key: string): LineRow[] => [
  ...rows.map((r) => ({
    id: `${key}-${r.accountId}`,
    cells: { account: accountCell(r.code, r.name), amount: money(r.amountCents) },
  })),
  { id: `${key}-sum`, tone: 'sum' as const, cells: { account: label, amount: money(total) } },
];

function Journal({
  mayReverse,
  toast,
  focus,
}: {
  mayReverse: boolean;
  toast: (m: string) => void;
  /** A journal entry a document link opened: the dates move to its month, and it opens. */
  focus?: string;
}) {
  const [from, setFrom] = useState(monthOf(todayIn())[0]);
  const [to, setTo] = useState(todayIn());
  const [open, setOpen] = useState<string | null>(focus ?? null);
  const [found, setFound] = useState(!focus);
  useEffect(() => {
    if (!focus) return;
    client
      .call<JournalEntry>('journal.get', { id: focus })
      .then((e) => {
        const [start, end] = monthOf(e.postingDate);
        setFrom(start);
        setTo(end);
        setFound(true);
      })
      .catch(() => {
        toast('That journal entry can’t be opened here.');
        setFound(true);
      });
  }, [focus, toast]);
  const j = useQuery<{ entries: JournalEntry[] }>('journal.list', found ? { from, to, limit: 200 } : null);
  useDocFocus(focus, !!j.data && found);
  const [reversing, setReversing] = useState<JournalEntry | null>(null);
  return (
    <Section title="Journal">
      {/* at the end of the name's line, as the panel's aside was */}
      <div className="bp-ledger-tools">
        <TextField label="From" hideLabel type="date" width="date" value={from} onChange={setFrom} />
        <TextField label="To" hideLabel type="date" width="date" value={to} onChange={setTo} />
      </div>
      {!j.data ? (
        <Waiting error={j.error} what="the journal" onRetry={() => void j.reload()} />
      ) : j.data.entries.length === 0 ? (
        <>
          <p className="bp-ledger-say">Nothing posted in these dates.</p>
          <BeforeTheSwitch />
        </>
      ) : (
        <RecordList label="Journal entries">
          {[...j.data.entries].sort(byDateThenNumber).map((e) => {
            const amount = e.lines.reduce((s, l) => s + l.debitCents, 0);
            const why = notReversibleWhy(e);
            return (
              <div key={e.id} data-doc={e.id}>
                <RecordRow
                  id={`je-${e.id}`}
                  when={e.number}
                  title={e.memo}
                  sub={
                    // A late document posts in the first open month; both dates show (Q48, QA-263).
                    `${dayText(e.postingDate)}${
                      e.documentDate && e.documentDate !== e.postingDate
                        ? ` · document ${dayText(e.documentDate)}`
                        : ''
                    } · ${entryKind(e.rule ?? e.source)}`
                  }
                  end={money(amount)}
                  label={`${e.number}, ${e.memo}`}
                  isOpen={open === e.id}
                  onToggle={() => setOpen(open === e.id ? null : e.id)}
                  expanded={
                    <div className="bp-ledger-entry">
                      <LineTable
                        label={`${e.number}’s lines`}
                        head={false}
                        columns={[
                          { id: 'side', label: 'Side' },
                          { id: 'account', label: 'Account' },
                          { id: 'dr', label: 'Debit', align: 'end' },
                          { id: 'cr', label: 'Credit', align: 'end' },
                        ]}
                        rows={e.lines.map((l) => ({
                          id: String(l.lineNo),
                          cells: {
                            side: l.debitCents ? 'Dr' : 'Cr',
                            account: (
                              <>
                                {accountCell(l.accountCode, l.accountName)}
                                {l.memo ? <span className="bp-ledger-code"> · {l.memo}</span> : null}
                              </>
                            ),
                            dr: l.debitCents ? money(l.debitCents) : null,
                            cr: l.creditCents ? money(l.creditCents) : null,
                          },
                        }))}
                      />
                      {mayReverse && reversible(e) ? (
                        <div className="bp-ledger-acts">
                          <Button onPress={() => setReversing(e)}>{`Reverse ${e.number}`}</Button>
                        </div>
                      ) : why ? (
                        <p className="bp-ledger-say">{why}</p>
                      ) : null}
                    </div>
                  }
                />
              </div>
            );
          })}
        </RecordList>
      )}
      <Reverse
        entry={reversing}
        onClose={() => setReversing(null)}
        onReversed={(said) => {
          toast(said);
          void j.reload();
        }}
      />
    </Section>
  );
}

/** journal.reverse: the reason (required) and the day it posts (optional), then the mirror entry. */
function Reverse({
  entry,
  onClose,
  onReversed,
}: {
  entry: JournalEntry | null;
  onClose: () => void;
  onReversed: (said: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [date, setDate] = useState('');
  const [shown, setShown] = useState<string | null>(null);
  if ((entry?.id ?? null) !== shown) {
    setShown(entry?.id ?? null);
    setReason('');
    setDate('');
  }
  const amount = entry ? entry.lines.reduce((s, l) => s + l.debitCents, 0) : 0;
  return (
    <FormDialog
      isOpen={!!entry}
      onClose={onClose}
      title={entry ? `Reverse ${entry.number}?` : 'Reverse'}
      submitLabel={entry ? `Reverse ${entry.number}` : 'Reverse'}
      isDirty={!!reason.trim() || !!date}
      isSubmitDisabled={!reason.trim()}
      note={
        entry
          ? `A new entry posts the mirror image of ${entry.number} (${money(amount)}, ${entry.memo}): every debit becomes a credit. ${entry.number} itself stays in the journal, marked reversed.`
          : null
      }
      onSubmit={async () => {
        if (!entry) return;
        try {
          const r = await client.call<{ number: string; postingDate: string; moved: boolean }>(
            'journal.reverse',
            { entryId: entry.id, reason: reason.trim(), ...(date ? { date } : {}) },
          );
          onReversed(reversedText(r, dayText));
        } catch (e) {
          throw new Error(errorText(e), { cause: e });
        }
      }}
    >
      <TextField label="Why" isRequired multiline maxLength={300} value={reason} onChange={setReason} />
      <TextField
        label="Date of the reversal (optional)"
        type="date"
        width="date"
        value={date}
        onChange={setDate}
        description="Today if left blank. A date in a closed month posts in the first open one."
      />
    </FormDialog>
  );
}

function Trial() {
  const t = useQuery<TrialBalance>('reports.trialBalance', {});
  const d = t.data;
  return (
    <Section
      title={d ? `Trial balance · ${dayText(d.asOf)}` : 'Trial balance'}
      {...(d ? { state: standing(d.balanced, 'Balanced', 'Out of balance') } : {})}
    >
      {!d ? (
        <Waiting error={t.error} what="the trial balance" onRetry={() => void t.reload()} />
      ) : (
        <>
          <LineTable
            label="Trial balance"
            columns={[
              { id: 'account', label: 'Account' },
              { id: 'dr', label: 'Debit', align: 'end' },
              { id: 'cr', label: 'Credit', align: 'end' },
            ]}
            rows={d.rows.map((r) => ({
              id: r.accountId,
              cells: {
                account: accountCell(r.code, r.name),
                dr: r.debitCents ? money(r.debitCents) : null,
                cr: r.creditCents ? money(r.creditCents) : null,
              },
            }))}
            foot={{ account: 'Total', dr: money(d.totalDebitCents), cr: money(d.totalCreditCents) }}
          />
          {d.totalDebitCents === 0 && <BeforeTheSwitch />}
        </>
      )}
    </Section>
  );
}

function PL() {
  const [[from, to], setRange] = useState(monthOf(todayIn()));
  const p = useQuery<ProfitAndLoss>('reports.profitAndLoss', { from, to });
  const d = p.data;
  return (
    <Section title={`Profit and loss · ${monthName(from)}`}>
      <div className="bp-ledger-tools">
        <Button size="sm" onPress={() => setRange(previousMonth(from))}>
          ‹ Earlier
        </Button>
        <Button size="sm" onPress={() => setRange(monthOf(todayIn()))}>
          This month
        </Button>
      </div>
      {!d ? (
        <Waiting error={p.error} what="profit and loss" onRetry={() => void p.reload()} />
      ) : (
        <>
          <LineTable
            label={`Profit and loss, ${monthName(from)}`}
            head={false}
            columns={ACCOUNT_COLUMNS}
            rows={[
              ...sectionRows(d.income, d.incomeCents, 'Income', 'in'),
              ...sectionRows(d.costOfSales, d.costOfSalesCents, 'Cost of sales', 'cos'),
              {
                id: 'gross',
                tone: 'total',
                cells: { account: 'Gross profit', amount: money(d.grossProfitCents) },
              },
              ...sectionRows(d.expenses, d.expensesCents, 'Expenses', 'ex'),
              { id: 'net', tone: 'total', cells: { account: 'Net profit', amount: money(d.netProfitCents) } },
            ]}
          />
          {!d.incomeCents && !d.costOfSalesCents && !d.expensesCents && <BeforeTheSwitch />}
        </>
      )}
    </Section>
  );
}

function BS() {
  const b = useQuery<BalanceSheet>('reports.balanceSheet', {});
  const d = b.data;
  return (
    <Section
      title={d ? `Balance sheet · ${dayText(d.asOf)}` : 'Balance sheet'}
      {...(d ? { state: standing(d.balanced, 'Balances', 'Does not balance') } : {})}
    >
      {!d ? (
        <Waiting error={b.error} what="the balance sheet" onRetry={() => void b.reload()} />
      ) : (
        <LineTable
          label="Balance sheet"
          head={false}
          columns={ACCOUNT_COLUMNS}
          rows={[
            ...sectionRows(d.assets, d.assetsCents, 'Assets', 'a'),
            ...sectionRows(d.liabilities, d.liabilitiesCents, 'Liabilities', 'l'),
            ...sectionRows(
              [
                ...d.equity,
                {
                  accountId: 're',
                  code: '',
                  name: 'Retained earnings',
                  amountCents: d.retainedEarningsCents,
                },
                {
                  accountId: 'ce',
                  code: '',
                  name: 'Earnings this year',
                  amountCents: d.currentEarningsCents,
                },
              ],
              d.equityCents,
              'Equity',
              'e',
            ),
          ]}
        />
      )}
    </Section>
  );
}

interface CashFlowReport {
  netProfitCents: number;
  operating: ReportRow[];
  investing: ReportRow[];
  financing: ReportRow[];
  operatingCents: number;
  investingCents: number;
  financingCents: number;
  netChangeCents: number;
  openingCashCents: number;
  closingCashCents: number;
  ties: boolean;
}

function CashFlow() {
  const [from, to] = monthOf(todayIn());
  const c = useQuery<CashFlowReport>('reports.cashFlow', { from, to });
  const d = c.data;
  return (
    <Section
      title={`Cash flow · ${monthName(from)}`}
      {...(d ? { state: standing(d.ties, 'Ties to the bank', 'Does not tie') } : {})}
    >
      {!d ? (
        <Waiting error={c.error} what="cash flow" onRetry={() => void c.reload()} />
      ) : (
        <LineTable
          label={`Cash flow, ${monthName(from)}`}
          head={false}
          columns={ACCOUNT_COLUMNS}
          rows={[
            { id: 'np', cells: { account: 'Net profit', amount: money(d.netProfitCents) } },
            ...sectionRows(d.operating, d.operatingCents, 'From operations', 'op'),
            ...sectionRows(d.investing, d.investingCents, 'Investing', 'inv'),
            ...sectionRows(d.financing, d.financingCents, 'Financing', 'fin'),
            {
              id: 'chg',
              tone: 'total',
              cells: { account: 'Change in cash', amount: money(d.netChangeCents) },
            },
            {
              id: 'cash',
              cells: {
                account: 'Cash at the start · end',
                amount: (
                  <span className="bp-ledger-span">
                    {money(d.openingCashCents)} → {money(d.closingCashCents)}
                  </span>
                ),
              },
            },
          ]}
        />
      )}
    </Section>
  );
}

interface JobCostingReport {
  rows: {
    jobId: string;
    jobNumber: string;
    revenueCents: number;
    costOfSalesCents: number;
    /** Paid labour with its burden: in gross profit, so it's in the cost here too. */
    labourCents?: number;
    grossProfitCents: number;
    marginPct: number | null;
  }[];
  grossProfitCents: number;
}

function JobCosting() {
  const [from, to] = monthOf(todayIn());
  const j = useQuery<JobCostingReport>('reports.jobCosting', { from, to });
  const d = j.data;
  return (
    <Section title={`Job costing · ${monthName(from)}`}>
      {/* who sees it, not where it stands: a muted word at the end of the name's line, as the panel's aside was */}
      <div className="bp-ledger-tools">
        <Label>Owner only</Label>
      </div>
      {!d ? (
        <Waiting error={j.error} what="job costing" onRetry={() => void j.reload()} />
      ) : d.rows.length === 0 ? (
        <>
          <p className="bp-ledger-say">No job revenue this month.</p>
          <BeforeTheSwitch />
        </>
      ) : (
        <LineTable
          label={`Job costing, ${monthName(from)}`}
          columns={[
            { id: 'job', label: 'Job' },
            { id: 'rev', label: 'Revenue', align: 'end' },
            { id: 'cost', label: 'Cost', align: 'end' },
            { id: 'gp', label: 'Gross profit', align: 'end' },
            { id: 'margin', label: 'Margin', align: 'end' },
          ]}
          rows={d.rows.map((r) => ({
            id: r.jobId,
            cells: {
              job: `#${r.jobNumber}`,
              rev: money(r.revenueCents),
              cost: money(r.costOfSalesCents + (r.labourCents ?? 0)),
              gp: money(r.grossProfitCents),
              margin: r.marginPct === null ? '—' : `${Math.round(r.marginPct)}%`,
            },
          }))}
          foot={{ job: 'Total', gp: money(d.grossProfitCents) }}
        />
      )}
    </Section>
  );
}

interface DraftLine {
  key: number;
  accountId: string;
  debit: string;
  credit: string;
}
const twoLines = (): DraftLine[] => [
  { key: 1, accountId: '', debit: '', credit: '' },
  { key: 2, accountId: '', debit: '', credit: '' },
];

function NewEntry({ toast }: { toast: (m: string) => void }) {
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', {});
  const periods = useQuery<{ periods: Period[] }>('periods.list', {});
  const act = useAct(toast, () => undefined);
  const [date, setDate] = useState(todayIn());
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<DraftLine[]>(twoLines);
  const totals = useMemo(() => {
    const d = lines.reduce((s, l) => s + (parseMoney(l.debit) ?? 0), 0);
    const c = lines.reduce((s, l) => s + (parseMoney(l.credit) ?? 0), 0);
    return { d, c };
  }, [lines]);
  const set = (k: number, patch: Partial<DraftLine>) =>
    setLines(lines.map((l) => (l.key === k ? { ...l, ...patch } : l)));
  const ready =
    memo.trim() &&
    totals.d > 0 &&
    totals.d === totals.c &&
    lines.every((l) => !(parseMoney(l.debit) || parseMoney(l.credit)) || l.accountId);
  const submit = async (moveToOpen = false) => {
    const r = await act.run<{ number: string; moved: boolean; postingDate: string }>('journal.post', {
      date,
      memo: memo.trim(),
      ...(moveToOpen ? { postInFirstOpenPeriod: true } : {}),
      lines: lines
        .filter((l) => l.accountId && (parseMoney(l.debit) || parseMoney(l.credit)))
        .map((l) => ({
          accountId: l.accountId,
          ...(parseMoney(l.debit) ? { debitCents: parseMoney(l.debit)! } : {}),
          ...(parseMoney(l.credit) ? { creditCents: parseMoney(l.credit)! } : {}),
        })),
    });
    if (r) {
      toast(
        r.moved
          ? `Posted ${r.number} on ${dayText(r.postingDate)}, the first open day.`
          : `Posted ${r.number}.`,
      );
      setMemo('');
      setLines(twoLines());
    }
  };
  const balanced = totals.d === totals.c;
  // Balanced or Off by, the Section's state word: a problem while it's off
  const state: { mark: MarkState; word: string } = balanced
    ? { mark: totals.d > 0 ? 'done' : 'waiting', word: 'Balanced' }
    : { mark: 'problem', word: `Off by ${money(totals.d - totals.c)}` };
  const hint = periods.data ? closedMonthHint(date, periods.data.periods) : null;
  return (
    <Section title="New journal entry" {...(accounts.data ? { state } : {})}>
      {!accounts.data ? (
        <Waiting error={accounts.error} what="the chart of accounts" onRetry={() => void accounts.reload()} />
      ) : (
        <>
          <div className="bp-ledger-head">
            <TextField
              label="Date"
              type="date"
              width="date"
              value={date}
              onChange={setDate}
              {...(hint ? { description: `${hint}.` } : {})}
            />
            <TextField label="Memo" value={memo} onChange={setMemo} placeholder="What this entry is for" />
          </div>
          <ul className="bp-ledger-lines" aria-label="The entry’s lines">
            {lines.map((l) => (
              <li key={l.key} className="bp-ledger-line">
                <SelectField
                  label="Account"
                  hideLabel
                  value={l.accountId}
                  onChange={(v) => set(l.key, { accountId: v })}
                  options={[
                    { value: '', label: 'Account…' },
                    ...accounts
                      .data!.accounts.filter((a) => a.active)
                      .map((a) => ({ value: a.id, label: `${a.code} ${a.name}` })),
                  ]}
                />
                <TextField
                  label="Debit"
                  hideLabel
                  inputMode="decimal"
                  placeholder="Debit"
                  value={l.debit}
                  onChange={(v) => set(l.key, { debit: v, credit: '' })}
                />
                <TextField
                  label="Credit"
                  hideLabel
                  inputMode="decimal"
                  placeholder="Credit"
                  value={l.credit}
                  onChange={(v) => set(l.key, { credit: v, debit: '' })}
                />
              </li>
            ))}
          </ul>
          <div className="bp-ledger-acts">
            <Link
              onPress={() => setLines([...lines, { key: Date.now(), accountId: '', debit: '', credit: '' }])}
            >
              Add a line
            </Link>
            <Button variant="primary" isDisabled={!ready || act.busy} onPress={() => void submit()}>
              Post
            </Button>
            <Button isDisabled={!ready || act.busy} onPress={() => void submit(true)}>
              Post in the first open month
            </Button>
          </div>
        </>
      )}
    </Section>
  );
}

function Close({
  held,
  toast,
  go,
  timeZone,
}: {
  held: readonly string[];
  toast: (m: string) => void;
  go: (part: BooksPart) => void;
  timeZone?: string;
}) {
  const [, end] = previousMonth(todayIn(timeZone));
  const c = useQuery<CloseChecksProps>('periods.closeChecks', { date: end });
  const months = useQuery<{ periods: Period[] }>('periods.list', {});
  const act = useAct(toast, async () => {
    await Promise.all([c.reload(), months.reload()]);
  });
  const [reviewed, setReviewed] = useState(false);
  const [reopening, setReopening] = useState<Period | null>(null);
  const payroll = seesPayroll(held);
  const mayReopen = holdsAny(held, REOPEN_PERMS);
  const latest = months.data ? reopenable(months.data.periods) : null;
  const reopen = async (reason: string) => {
    if (!reopening) return;
    try {
      await client.call('periods.reopen', { date: reopening.startDate, reason: reason.trim() });
    } catch (e) {
      throw new Error(errorText(e), { cause: e });
    }
    toast(`${monthName(reopening.startDate)} is open again. Close it when you’re done.`);
    setReopening(null);
    await Promise.all([c.reload(), months.reload()]);
  };
  return (
    <>
      <Section title={c.data ? `Close ${monthWords(c.data.period.startDate)}` : 'Close last month'}>
        {!c.data ? (
          <Waiting error={c.error} what="the month-end checks" onRetry={() => void c.reload()} />
        ) : (
          <CloseChecklist
            data={c.data}
            host={host}
            timeZone={timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
            reviewed={reviewed}
            onReviewed={setReviewed}
            busy={act.busy}
            onFix={(key) => {
              const part = FIX_PART[key];
              if (part && (part !== 'payroll' || payroll)) go(part);
            }}
            onClose={() =>
              void act.run(
                'periods.close',
                { date: end, reviewedAgingAndAccruals: true },
                `${monthName(c.data!.period.startDate)} is closed.`,
              )
            }
          />
        )}
      </Section>
      <Section title="Months">
        {!months.data ? (
          <Waiting error={months.error} what="the months" onRetry={() => void months.reload()} />
        ) : months.data.periods.length === 0 ? (
          <p className="bp-ledger-say">Nothing posted yet, so no months to show.</p>
        ) : (
          <RowList
            label="Months"
            rows={monthRows(
              months.data.periods.map((p) => ({
                id: p.id,
                month: monthName(p.startDate),
                closed: p.status === 'closed',
                ...(p.closedAt ? { closedOn: closedOn(p.closedAt) } : {}),
              })),
              mayReopen && latest ? { id: latest.id, onPress: () => setReopening(latest) } : null,
            )}
            {...(!mayReopen && latest ? { note: 'Only the owner reopens a closed month.' } : {})}
          />
        )}
      </Section>
      <ConfirmDialog
        isOpen={!!reopening}
        onClose={() => setReopening(null)}
        title={reopening ? `Reopen ${monthName(reopening.startDate)}?` : 'Reopen'}
        confirmLabel={reopening ? `Reopen ${monthName(reopening.startDate)}` : 'Reopen'}
        reason={{ label: 'Why', missing: 'Say why it’s reopened.', maxLength: 500 }}
        onConfirm={reopen}
      >
        {reopening ? reopenWarning(monthName(reopening.startDate)) : null}
      </ConfirmDialog>
    </>
  );
}

/** Saves one CSV the browser's way: a download, under the name the pack gives it (with its dates). */
function save(file: PackFile) {
  const url = URL.createObjectURL(new Blob([file.content], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function AccountantPack() {
  const today = todayIn();
  const [from, setFrom] = useState(monthOf(today)[0]);
  const [to, setTo] = useState(today);
  const [files, setFiles] = useState<PackFile[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const make = async () => {
    const problem = rangeProblem(from, to);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ files: PackFile[] }>('exports.accountantPack', { from, to });
      setFiles(r.files);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const rows: Row[] = (files ?? []).map((f) => {
    const name = packFileLabel(f.name);
    return {
      id: f.name,
      name,
      lines: [{ text: rowsText(f.content) }],
      end: { action: 'Save', label: `Save ${name.toLowerCase()}`, onPress: () => save(f) },
    };
  });
  return (
    <Section title="Accountant pack">
      <p className="bp-ledger-say">
        What an accountant asks for at month or year end: the trial balance, profit and loss, balance sheet,
        cash flow, the general ledger detail, receivables aging, filed tax returns and bank reconciliations.
        Each is a CSV. Making a pack is recorded in the audit log.
      </p>
      <form
        className="bp-ledger-dates"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void make();
        }}
      >
        <TextField
          label="From"
          type="date"
          width="date"
          value={from}
          onChange={(v) => {
            setFrom(v);
            setFiles(null);
          }}
        />
        <TextField
          label="To"
          type="date"
          width="date"
          value={to}
          onChange={(v) => {
            setTo(v);
            setFiles(null);
          }}
        />
        <div className="bp-ledger-acts">
          <Button type="submit" isBusy={busy}>
            {busy ? 'Making the pack…' : 'Make the pack'}
          </Button>
        </div>
      </form>
      {error ? (
        <p className="bp-ledger-bad" role="alert">
          <span>{error}</span>
        </p>
      ) : null}
      {files ? (
        files.length ? (
          <>
            <RowList label="The pack’s files" rows={rows} />
            <div className="bp-ledger-acts">
              <Button
                variant="primary"
                onPress={() => files.forEach((f, i) => setTimeout(() => save(f), i * 250))}
              >
                {`Save all ${files.length}`}
              </Button>
            </div>
          </>
        ) : (
          <p className="bp-ledger-say">Nothing in these dates.</p>
        )
      ) : null}
    </Section>
  );
}

export function LedgerPage({
  nav,
  held,
  toast,
  go,
  timeZone,
  focus,
  start,
}: {
  nav: NavProps;
  held: readonly string[];
  toast: (m: string) => void;
  /** Opens another part of Books (where a month-end check is fixed). */
  go: (part: BooksPart) => void;
  timeZone?: string;
  /** A journal entry a document link opened. */
  focus?: string;
  /** The view it opens on (a story's); otherwise profit and loss. */
  start?: LedgerView;
}) {
  // job costing (reports.jobCosting): finance.margins.view, the owner's or a role of theirs that gives it
  const owner = held.includes('finance.margins.view');
  // a journal entry's link: the journal; back from the provider: how connecting went
  const [tab, setTab] = useState<Tab>(focus ? 'journal' : ledgerReturnUntold() ? 'connect' : (start ?? 'pl'));
  const [returned] = useState(ledgerReturnUntold);
  const crumb = usePageCrumb(nav);
  const tabs: [Tab, string][] = [
    ['pl', 'Profit and loss'],
    ['bs', 'Balance sheet'],
    ['cash', 'Cash flow'],
    ['trial', 'Trial balance'],
    ...(owner ? ([['jobs', 'Job costing']] as [Tab, string][]) : []),
    ['journal', 'Journal'],
    ['entry', 'New entry'],
    ['close', 'Close a month'],
    ...(holdsAny(held, PACK_PERMS) ? ([['pack', 'Accountant pack']] as [Tab, string][]) : []),
    ['connect', 'Accounting system'],
  ];
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <div className="bp-ledger">
          <PageHead
            {...(crumb ? { crumb } : {})}
            title={PART.bp ?? PART.rail}
            lede={PART.lede}
            tabs={{
              label: 'Reports',
              items: tabs.map(([t, label]) => ({ label, isCurrent: tab === t, onPress: () => setTab(t) })),
            }}
          />
          {tab === 'journal' && (
            <Journal mayReverse={holdsAny(held, REVERSE_PERMS)} toast={toast} {...(focus ? { focus } : {})} />
          )}
          {tab === 'trial' && <Trial />}
          {tab === 'pl' && <PL />}
          {tab === 'bs' && <BS />}
          {tab === 'cash' && <CashFlow />}
          {tab === 'jobs' && owner && <JobCosting />}
          {tab === 'entry' && <NewEntry toast={toast} />}
          {tab === 'close' && <Close held={held} toast={toast} go={go} {...(timeZone ? { timeZone } : {})} />}
          {tab === 'pack' && <AccountantPack />}
          {tab === 'connect' && !returned && (
            <Section title="Accounting system">
              <p className="bp-ledger-say" role="note">
                The accounting system moved to{' '}
                <Link href="#/settings/connections">Setup › Connected systems</Link>.
              </p>
            </Section>
          )}
          {tab === 'connect' && returned && (
            // back from the provider: how connecting went, in Books' own panel (its region) until it's ported
            <>
              <p className="bp-ledger-say" role="note">
                The accounting system moved to{' '}
                <Link href="#/settings/connections">Setup › Connected systems</Link>.
              </p>
              <div className="books">
                <div className="books-main">
                  <Connections toast={toast} {...(timeZone ? { timeZone } : {})} />
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
